/**
 * Modal.tsx - Smart Modal with Enhanced DatePicker Support
 *
 * Enhanced to provide proper detection attributes for the DatePicker component
 * and optimized spacing for expandable components like date pickers.
 */

import React, { useEffect, useState, useRef } from 'react';
import { X } from 'lucide-react';
import { clsx } from 'clsx';

export interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    children: React.ReactNode;
    size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
    closeOnClickOutside?: boolean;
    showCloseButton?: boolean;
    className?: string;
}

export const Modal: React.FC<ModalProps> = ({
                                                isOpen,
                                                onClose,
                                                title,
                                                children,
                                                size = 'lg',
                                                closeOnClickOutside = true,
                                                showCloseButton = true,
                                                className
                                            }) => {
    const [expandedHeight, setExpandedHeight] = useState(0);
    const contentRef = useRef<HTMLDivElement>(null);
    const modalRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, onClose]);

    // Monitor for expandable components (like date pickers)
    useEffect(() => {
        if (!isOpen || !contentRef.current) return;

        const observeExpandables = () => {
            // Look for common expandable component selectors
            const expandableSelectors = [
                '[role="dialog"]', // Date picker dialogs
                '[role="listbox"]', // Select dropdowns
                '.react-datepicker', // Common date picker class
                '[data-headlessui-state="open"]', // Headless UI open states
                '[aria-expanded="true"]', // ARIA expanded elements
            ];

            let maxExpandedHeight = 0;
            let hasExpandedComponents = false;

            expandableSelectors.forEach(selector => {
                const elements = contentRef.current!.querySelectorAll(selector);
                elements.forEach(element => {
                    const rect = element.getBoundingClientRect();
                    if (rect.height > 0) {
                        hasExpandedComponents = true;
                        maxExpandedHeight = Math.max(maxExpandedHeight, rect.height);
                    }
                });
            });

            if (hasExpandedComponents && maxExpandedHeight > expandedHeight) {
                setExpandedHeight(maxExpandedHeight);
            }
        };

        // Initial check
        observeExpandables();

        // Set up mutation observer for dynamic content
        const observer = new MutationObserver(() => {
            observeExpandables();
        });

        observer.observe(contentRef.current, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['class', 'style', 'aria-expanded', 'data-headlessui-state']
        });

        return () => {
            observer.disconnect();
        };
    }, [isOpen, expandedHeight]);

    const handleBackdropClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget && closeOnClickOutside) {
            onClose();
        }
    };

    if (!isOpen) return null;

    const sizeClasses = {
        sm: 'max-w-md',
        md: 'max-w-lg',
        lg: 'max-w-2xl',
        xl: 'max-w-4xl',
        '2xl': 'max-w-6xl',
    };

    // Calculate dynamic height based on content and expanded components
    const dynamicStyle = expandedHeight > 0 ? {
        minHeight: `${Math.min(expandedHeight + 200, window.innerHeight * 0.9)}px`
    } : {};

    return (
        <div
            className="fixed inset-0 z-50 overflow-y-auto"
            aria-labelledby="modal-title"
            role="dialog"
            aria-modal="true"
        >
            {/* Backdrop */}
            <div
                className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
                onClick={handleBackdropClick}
            >
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" />

                {/* Center modal */}
                <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
                    &#8203;
                </span>

                {/* Modal panel */}
                <div
                    ref={modalRef}
                    className={clsx(
                        'inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle w-full',
                        sizeClasses[size],
                        className
                    )}
                    style={dynamicStyle}
                    data-modal="true" // Important: This attribute helps DatePicker detect modal context
                >
                    {/* Header */}
                    <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4 border-b border-gray-200">
                        <div className="flex items-center justify-between">
                            <h3
                                id="modal-title"
                                className="text-lg leading-6 font-medium text-gray-900"
                            >
                                {title}
                            </h3>
                            {showCloseButton && (
                                <button
                                    type="button"
                                    className="bg-white rounded-md text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                                    onClick={onClose}
                                >
                                    <span className="sr-only">Close</span>
                                    <X className="h-6 w-6" />
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Content */}
                    <div
                        ref={contentRef}
                        className="bg-white px-4 pt-5 pb-4 sm:p-6 relative"
                        style={{ minHeight: '200px' }} // Ensure minimum space for date pickers
                    >
                        {children}
                    </div>
                </div>
            </div>
        </div>
    );
};